import { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Read the signed-in user's Cognito groups (= roles) from the ID token. `cognito:groups` can
 * arrive as an array or a comma-separated string depending on the token/SDK, so normalise to a
 * string[]. Returns `null` while loading so callers can distinguish "not yet known" from "no
 * roles" and avoid a flash of gated UI.
 */
export function useRoles(deps: readonly unknown[] = []): string[] | null {
  const [roles, setRoles] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Re-read whenever `deps` change (e.g. once the user signs in) — the token is not yet
    // available on the App's first mount (pre-login), so a one-time mount read would cache [].
    setRoles(null);
    fetchAuthSession()
      .then((s) => {
        if (cancelled) return;
        const raw = s.tokens?.idToken?.payload?.['cognito:groups'] as
          | string[]
          | string
          | undefined;
        const groups = Array.isArray(raw)
          ? raw
          : typeof raw === 'string'
            ? raw.split(',').map((g) => g.trim()).filter(Boolean)
            : [];
        setRoles(groups);
      })
      .catch(() => {
        if (!cancelled) setRoles([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return roles;
}

export function hasRole(roles: string[] | null, ...allowed: string[]): boolean {
  return !!roles && roles.some((r) => allowed.includes(r));
}
